import { useState } from 'react';
import { useCurrentUser } from '../../context/CurrentUserContext';
import RoleGate from '../RoleGate';
import PillInput from './PillInput';

// Only domain we forward for today — turn this into a dropdown if more get added.
// contentsquare.dev is Security's own domain (needed for their red-team/phishing-sim work);
// new forwarder rules go on contentsquare.cloud instead (QA-6909). Existing contentsquare.dev
// rules keep working unchanged -- this only affects the domain suggested for new/edited rules.
const FORWARDER_DOMAIN = 'contentsquare.cloud';

function sanitizePrefix(raw) {
  const withoutDomain = raw.endsWith(`@${FORWARDER_DOMAIN}`)
    ? raw.slice(0, -(FORWARDER_DOMAIN.length + 1))
    : raw;
  return withoutDomain.split('@')[0];
}

function MailForwarderRuleForm({ rule, onSave, onCancel, saving, error }) {
  const currentUser = useCurrentUser();
  const isNew = !rule.id;
  const isAdmin = currentUser?.hubRole === 'admin';
  const ownAddress = currentUser?.email ? [currentUser.email] : [];

  const [form, setForm] = useState({
    sourceAddressPrefix: sanitizePrefix(rule.sourceAddress ?? ''),
    destinationAddresses: isAdmin ? (rule.destinationAddresses ?? (isNew ? ownAddress : [])) : ownAddress,
    enabled: rule.enabled ?? true,
    visibility: isAdmin ? (rule.visibility ?? 'private') : 'private',
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Rules already match plus-tagged variants of their base address automatically (QA-7056), so a
  // "+" here would only ever create a dead rule that can't match anything — block it rather than
  // silently stripping what the admin typed.
  const hasPlusTag = form.sourceAddressPrefix.includes('+');

  const handleSubmit = e => {
    e.preventDefault();
    if (hasPlusTag) return;
    onSave({
      sourceAddress: `${form.sourceAddressPrefix.trim()}@${FORWARDER_DOMAIN}`,
      destinationAddresses: isAdmin ? form.destinationAddresses : ownAddress,
      enabled: form.enabled,
      visibility: isAdmin ? form.visibility : 'private',
    });
  };

  return (
    <div className="cf-overlay">
      <div className="cf-modal">
        <div className="cf-header">
          <h2>{isNew ? 'New Mail Forwarder' : 'Edit Mail Forwarder'}</h2>
          <button className="cf-close" type="button" onClick={onCancel}>✕</button>
        </div>

        <form className="cf-form" onSubmit={handleSubmit}>
          {error && <div className="admin-error">{error}</div>}

          <label className="cf-label">Source Address <span className="cf-req">*</span></label>
          <div className="cf-domain-input">
            <input className="cf-input" required value={form.sourceAddressPrefix}
              onChange={e => set('sourceAddressPrefix', sanitizePrefix(e.target.value))}
              placeholder="alias" />
            <span className="cf-domain-suffix">@{FORWARDER_DOMAIN}</span>
          </div>
          {hasPlusTag && (
            <div className="cf-field-warning">
              Don&apos;t include a &quot;+&quot; — variants of this address (e.g. alias+anything) are already matched automatically. Use the base address without a &quot;+&quot;.
            </div>
          )}

          <label className="cf-label">Recipients <span className="cf-req">*</span></label>
          <RoleGate
            allow={['admin']}
            fallback={(
              <div className="pill-input">
                <span className="pill-input-tag">{currentUser?.email}</span>
              </div>
            )}
          >
            <PillInput
              value={form.destinationAddresses}
              onChange={addresses => set('destinationAddresses', addresses)}
              placeholder="Type an address and press Enter"
            />
          </RoleGate>

          <RoleGate allow={['admin']}>
            <label className="cf-label">Visibility</label>
            <select className="cf-input" value={form.visibility}
              onChange={e => set('visibility', e.target.value)}>
              <option value="private">Private (only you can manage it)</option>
              <option value="public">Public (any team member can manage it)</option>
            </select>
          </RoleGate>

          <label className="cf-section-check" style={{ marginTop: 12 }}>
            <input type="checkbox" checked={form.enabled}
              onChange={e => set('enabled', e.target.checked)} />
            Enabled
          </label>

          <div className="cf-actions">
            <button type="button" className="cf-btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="cf-btn-save" disabled={saving || form.destinationAddresses.length === 0 || hasPlusTag}>
              {saving ? 'Saving…' : isNew ? 'Create Forwarder' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MailForwarderRuleForm;
